import type { AvailabilityStatus, LineupStatus, MatchSiteType, OpponentRatingBasis, PlayerRatingBasis } from '../types';
import { isDesignatedSubstitute, type RosterRole } from './rosterRoles.ts';
import {
  normalizeCompatibility,
  normalizePairingRule,
  pairingRuleLabels,
  partnerPreferenceForPair,
  type PartnerCompatibility,
  type PartnerPairingRule,
} from './partnerPreferences.ts';

export type AdvancementType = 'advancing' | 'non_advancing';
export type ChangeSensitivity = 'flexible' | 'moderate' | 'conservative';
export type TentativePlayerPolicy = 'exclude_tentative' | 'standby_only' | 'allow_in_draft_with_warning';
export type SinglesPreference = 'yes' | 'if_possible' | 'no';
export type RecommendationPlanType = 'minimal_disruption' | 'balanced' | 'strongest_competitive';
export type RecommendationMode = 'single_match' | 'assignment_repair' | 'match_repair' | 'season_baseline' | 'remaining_season';
export type UnknownAvailabilityPolicy = 'exclude' | 'include_with_warning' | 'assume_available_for_planning';
export type SeasonRecommendationPlanType = 'balanced_season' | 'strongest_competitive_season' | 'participation_focused_season' | 'minimal_change_season';
export type SeasonPlanStatus = 'generated' | 'accepted' | 'superseded' | 'discarded';
export type PlanConfidence = 'High' | 'Moderate' | 'Low';
export type RatingConfidence = 'High' | 'Moderate' | 'Reduced' | 'Low';
export type RecommendationPolicy = 'block' | 'warn' | 'allow';
export type WarningSeverity = 'info' | 'warning' | 'critical';
export type DraftAssignmentPolicy = 'ignore' | 'provisional' | 'committed';
export type StandbyAssignmentPolicy = 'ignore' | 'context_only' | 'treat_as_assignment';
export type ConsecutiveSinglesMode = 'both_singles' | 'either_singles';
export type TargetMatchVariance = number;

export type SchedulingPolicyConfig = {
  policy: RecommendationPolicy;
  warningSeverity: WarningSeverity;
  warningPenalty: number;
};

export type MultiTeamSchedulingConfig = {
  overlappingSameDay: SchedulingPolicyConfig;
  nonOverlappingSameDay: SchedulingPolicyConfig;
  consecutiveDay: SchedulingPolicyConfig;
  consecutiveDayAway: SchedulingPolicyConfig;
  consecutiveDaySingles: SchedulingPolicyConfig & { mode: ConsecutiveSinglesMode };
  recovery: SchedulingPolicyConfig & { minimumHours: number };
  rollingWorkload: SchedulingPolicyConfig & { maxAssignments: number; windowDays: number };
  assignmentSources: {
    draftPolicy: DraftAssignmentPolicy;
    draftCertaintyMultiplier: number;
    standbyPolicy: StandbyAssignmentPolicy;
  };
  timing: {
    defaultMatchDurationMinutes: number;
    defaultTravelBufferMinutes: number;
  };
};

export type RecommendationStrategyProfile = {
  id?: string;
  name: string;
  advancementType: AdvancementType;
  version: number;
  competitivenessWeight: number;
  participationFairnessWeight: number;
  singlesOpportunityWeight: number;
  scheduleSpacingWeight: number;
  partnerCompatibilityWeight: number;
  homeAwayBalanceWeight: number;
  singlesEdrWeight: number;
  singlesUtrWeight: number;
  doublesEdrWeight: number;
  doublesUtrWeight: number;
  changeSensitivity: ChangeSensitivity;
  tentativePlayerPolicy: TentativePlayerPolicy;
  preferredPairBonus?: number;
  preferredPairSeparationPenalty?: number;
  stronglyPreferredPairBonus?: number;
  stronglyPreferredPairSeparationPenalty?: number;
  avoidPairingPenalty?: number;
  poorCompatibilityPenalty?: number;
  goodCompatibilityBonus?: number;
  excellentCompatibilityBonus?: number;
  multiTeamScheduling?: MultiTeamSchedulingConfig;
  enforceTargetMatches?: boolean;
  targetMatchVariance?: TargetMatchVariance;
  enforceMaxSit?: boolean;
  maxWeeksWithoutMatch?: number | null;
  usePartnerCompatibility?: boolean;
  usePartnerSchedulingPreferences?: boolean;
};

export type StrategyValidationResult = {
  valid: boolean;
  errors: string[];
  warnings: string[];
  schedulingTotal: number;
  singlesRatingTotal: number;
  doublesRatingTotal: number;
};

export type RecommendationPlayer = {
  id: string;
  firstName: string;
  lastName: string;
  sex: 'male' | 'female';
  availabilityStatus: AvailabilityStatus;
  rosterStatus: string;
  rosterRole?: RosterRole | string | null;
  estimatedDynamicRating: number | null;
  utrSinglesRating: number | null;
  utrDoublesRating: number | null;
  playerTier: string | null;
  targetMatches: number;
  completedPlayed: number;
  futureSingles: number;
  futureDoubles: number;
  singlesPlayed: number;
  homeMatches?: number;
  awayMatches?: number;
  neutralMatches?: number;
  totalAssignmentsForBalance?: number;
  /** ISO date of the player's most recent completed match for this team. */
  lastPlayedDate?: string | null;
  lastPlayedWeeksAgo?: number | null;
  seasonRemainingOpportunities?: number;
  /** Canonical player preference used for cross-team consecutive-day decisions. */
  backToBackTolerance?: 'no' | 'if_needed' | 'yes' | null;
  sameDayTolerance?: 'no' | 'if_needed' | 'yes' | null;
  minimumRecoveryHours?: number | null;
  /** Priority of the team currently being planned for this player. */
  teamPriority?: 'primary' | 'normal' | 'secondary' | null;
};

export type RecommendationPosition = {
  id: string;
  positionCode: string;
  displayOrder: number;
  lineType: string;
  requiredPlayerCount: number;
  requiredMaleCount: number;
  requiredFemaleCount: number;
};

export type RecommendationEntry = {
  id?: string;
  matchEventId: string;
  leagueConfigurationPositionId: string;
  positionCode: string;
  slotNumber: number;
  playerId: string;
};

export type RecommendationMatch = {
  id: string;
  teamId: string;
  seasonId: string;
  matchDate: string;
  matchTime?: string | null;
  arrivalDateTime?: string | null;
  matchStatus: string;
  lineupStatus: LineupStatus;
  matchSiteType: MatchSiteType;
  opponentName: string;
  opponentAverageEdr: number | null;
  opponentRatingBasis?: OpponentRatingBasis;
  opponentSubjectiveDifficulty?: number | null;
  planningPriority?: 'development' | 'normal' | 'important' | 'must_win';
  matchPhase?: 'regular_season' | 'postseason' | string | null;
};

export type SharedPlayerScheduleAssignment = {
  playerId: string;
  matchId: string;
  teamId: string;
  teamName?: string | null;
  opponentName?: string | null;
  matchDate: string;
  matchTime?: string | null;
  arrivalDateTime?: string | null;
  matchSiteType: MatchSiteType;
  facilityId?: string | null;
  facilityName?: string | null;
  positionCode?: string | null;
  assignmentType: 'singles' | 'doubles' | 'standby' | 'unknown';
  lineupStatus: LineupStatus;
  matchStatus: string;
  certainty: 'committed' | 'provisional' | 'context';
  source: 'lineup' | 'standby' | 'historical';
  /** Priority of this assignment's team for the player, when available. */
  teamPriority?: 'primary' | 'normal' | 'secondary' | null;
};

export type RecommendationInput = {
  match: RecommendationMatch;
  positions: RecommendationPosition[];
  players: RecommendationPlayer[];
  entries: RecommendationEntry[];
  partnerPreferences: Array<{
    workspace_id: string;
    player1_id: string;
    player2_id: string;
    compatibility: PartnerCompatibility | null;
    pairing_rule?: PartnerPairingRule | null;
    is_active?: boolean | null;
    notes?: string | null;
  }>;
  workspaceId: string;
  strategy: RecommendationStrategyProfile;
  playerRatingBasis?: PlayerRatingBasis;
  singlesPreferenceByPlayerId?: Record<string, SinglesPreference>;
  pinnedAssignmentIds?: string[];
  excludedPlayerMatchPairs?: Array<{ playerId: string; matchId: string }>;
  sharedPlayerSchedule?: SharedPlayerScheduleAssignment[];
  respectTargetMatchCounts?: boolean;
  crossTeamPolicyTeamIds?: string[];
};

export type RatingExplanation = {
  playerId: string;
  assignmentType: 'singles' | 'doubles';
  score: number;
  confidence: RatingConfidence;
  text: string;
};

export type RecommendationChange = {
  matchEventId: string;
  leagueConfigurationPositionId: string;
  positionCode: string;
  slotNumber: number;
  oldPlayerId: string | null;
  newPlayerId: string | null;
  changeType: 'added' | 'removed' | 'replaced' | 'unchanged';
  reason: string;
  scoreImpact: number;
};

export type RecommendationPlan = {
  id: string;
  planType: RecommendationPlanType;
  name: string;
  recommended: boolean;
  overallScore: number;
  objectiveScoreBreakdown: {
    competitiveness: number;
    participationFairness: number;
    singlesOpportunity: number;
    scheduleSpacing: number;
    partnerCompatibility: number;
    homeAwayBalance: number;
  };
  disruptionPenalty: number;
  changes: RecommendationChange[];
  benefits: string[];
  tradeoffs: string[];
  warnings: string[];
  explanations: string[];
  ratingExplanations: RatingExplanation[];
};

export type RecommendationAnalysis = {
  strategy: RecommendationStrategyProfile;
  generatedAt: string;
  currentStateSummary: {
    assignedPlayers: number;
    requiredPlayers: number;
    openSlots: number;
    readOnly: boolean;
    excludedBecauseUnavailable: number;
  };
  plans: RecommendationPlan[];
  unresolvedConstraints: string[];
};

export type SeasonPlanningMatch = RecommendationMatch & {
  entries: RecommendationEntry[];
  availabilityByPlayerId: Record<string, AvailabilityStatus | undefined>;
};

export type SeasonPlanningRequest = {
  mode: Extract<RecommendationMode, 'season_baseline' | 'remaining_season'>;
  teamId: string;
  seasonId: string;
  matches: SeasonPlanningMatch[];
  positions: RecommendationPosition[];
  players: RecommendationPlayer[];
  partnerPreferences: RecommendationInput['partnerPreferences'];
  workspaceId: string;
  strategy: RecommendationStrategyProfile;
  playerRatingBasis?: PlayerRatingBasis;
  singlesPreferenceByPlayerId?: Record<string, SinglesPreference>;
  selectedMatchIds?: string[];
  includePublished?: boolean;
  preserveExistingDraftAssignments?: boolean;
  preservePublishedAssignments?: boolean;
  preservePinnedAssignments?: boolean;
  pinnedAssignmentIds?: string[];
  excludedPlayerMatchPairs?: Array<{ playerId: string; matchId: string }>;
  unknownAvailabilityPolicy?: UnknownAvailabilityPolicy;
  includeTentativePlayers?: boolean;
};

export type AvailabilityScarcity = {
  playerId: string;
  remainingSelectedMatches: number;
  availableMatches: number;
  tentativeMatches: number;
  unknownMatches: number;
  eligibleSinglesOpportunities: number;
  matchesNeededToTarget: number;
  targetAchievable: boolean;
  urgencyScore: number;
  explanation: string;
};

export type PlayerSeasonProjection = {
  playerId: string;
  playerName: string;
  targetMatches: number;
  completedPlayed: number;
  proposedFuture: number;
  proposedSingles: number;
  proposedDoubles: number;
  projectedTotal: number;
  projectedDelta: number;
  scarcity: AvailabilityScarcity;
};

export type MatchSeasonProjection = {
  matchId: string;
  matchDate: string;
  opponentName: string;
  lineupStatus: LineupStatus;
  editable: boolean;
  confidence: PlanConfidence;
  confidenceDrivers: string[];
  changedAssignments: number;
  uncertainAssignments: number;
};

export type SeasonRecommendationPlan = {
  id: string;
  planType: SeasonRecommendationPlanType;
  name: string;
  recommended: boolean;
  overallScore: number;
  confidence: PlanConfidence;
  confidenceDrivers: string[];
  changes: RecommendationChange[];
  matchProjections: MatchSeasonProjection[];
  playerProjections: PlayerSeasonProjection[];
  materialIssues: string[];
  benefits: string[];
  tradeoffs: string[];
  warnings: string[];
  explanations: string[];
};

export type SeasonPlanningAnalysis = {
  mode: Extract<RecommendationMode, 'season_baseline' | 'remaining_season'>;
  strategy: RecommendationStrategyProfile;
  generatedAt: string;
  teamId: string;
  seasonId: string;
  includedMatchIds: string[];
  excludedMatches: Array<{ matchId: string; reason: string }>;
  availabilityScarcity: AvailabilityScarcity[];
  plans: SeasonRecommendationPlan[];
  unresolvedConstraints: string[];
};

const schedulingWeightKeys: Array<keyof RecommendationStrategyProfile> = [
  'competitivenessWeight',
  'participationFairnessWeight',
  'singlesOpportunityWeight',
  'scheduleSpacingWeight',
  'partnerCompatibilityWeight',
  'homeAwayBalanceWeight',
];

export const defaultMultiTeamSchedulingConfig: MultiTeamSchedulingConfig = {
  overlappingSameDay: { policy: 'block', warningSeverity: 'critical', warningPenalty: 60 },
  nonOverlappingSameDay: { policy: 'warn', warningSeverity: 'warning', warningPenalty: 18 },
  consecutiveDay: { policy: 'warn', warningSeverity: 'warning', warningPenalty: 14 },
  consecutiveDayAway: { policy: 'warn', warningSeverity: 'warning', warningPenalty: 8 },
  consecutiveDaySingles: { policy: 'warn', warningSeverity: 'warning', warningPenalty: 10, mode: 'both_singles' },
  recovery: { policy: 'warn', warningSeverity: 'warning', warningPenalty: 20, minimumHours: 16 },
  rollingWorkload: { policy: 'warn', warningSeverity: 'warning', warningPenalty: 16, maxAssignments: 3, windowDays: 7 },
  assignmentSources: {
    draftPolicy: 'provisional',
    draftCertaintyMultiplier: 0.65,
    standbyPolicy: 'context_only',
  },
  timing: {
    defaultMatchDurationMinutes: 150,
    defaultTravelBufferMinutes: 60,
  },
};

function numberOrDefault(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function policyOrDefault(value: unknown, fallback: RecommendationPolicy): RecommendationPolicy {
  return value === 'block' || value === 'warn' || value === 'allow' ? value : fallback;
}

function severityOrDefault(value: unknown, fallback: WarningSeverity): WarningSeverity {
  return value === 'info' || value === 'warning' || value === 'critical' ? value : fallback;
}

function normalizePolicyConfig(value: unknown, fallback: SchedulingPolicyConfig): SchedulingPolicyConfig {
  const record = (value && typeof value === 'object' ? value : {}) as Partial<SchedulingPolicyConfig>;
  return {
    policy: policyOrDefault(record.policy, fallback.policy),
    warningSeverity: severityOrDefault(record.warningSeverity, fallback.warningSeverity),
    warningPenalty: numberOrDefault(record.warningPenalty, fallback.warningPenalty),
  };
}

export function normalizeMultiTeamSchedulingConfig(value: unknown): MultiTeamSchedulingConfig {
  const record = (value && typeof value === 'object' ? value : {}) as Partial<MultiTeamSchedulingConfig>;
  const defaults = defaultMultiTeamSchedulingConfig;
  const consecutiveDaySingles = normalizePolicyConfig(record.consecutiveDaySingles, defaults.consecutiveDaySingles);
  const recovery = normalizePolicyConfig(record.recovery, defaults.recovery);
  const rollingWorkload = normalizePolicyConfig(record.rollingWorkload, defaults.rollingWorkload);
  const assignmentSources = (record.assignmentSources || {}) as Partial<MultiTeamSchedulingConfig['assignmentSources']>;
  const timing = (record.timing || {}) as Partial<MultiTeamSchedulingConfig['timing']>;
  return {
    overlappingSameDay: normalizePolicyConfig(record.overlappingSameDay, defaults.overlappingSameDay),
    nonOverlappingSameDay: normalizePolicyConfig(record.nonOverlappingSameDay, defaults.nonOverlappingSameDay),
    consecutiveDay: normalizePolicyConfig(record.consecutiveDay, defaults.consecutiveDay),
    consecutiveDayAway: normalizePolicyConfig(record.consecutiveDayAway, defaults.consecutiveDayAway),
    consecutiveDaySingles: {
      ...consecutiveDaySingles,
      mode: record.consecutiveDaySingles?.mode === 'either_singles' ? 'either_singles' : 'both_singles',
    },
    recovery: {
      ...recovery,
      minimumHours: Math.max(0, numberOrDefault(record.recovery?.minimumHours, defaults.recovery.minimumHours)),
    },
    rollingWorkload: {
      ...rollingWorkload,
      maxAssignments: Math.max(0, Math.round(numberOrDefault(record.rollingWorkload?.maxAssignments, defaults.rollingWorkload.maxAssignments))),
      windowDays: Math.max(1, Math.round(numberOrDefault(record.rollingWorkload?.windowDays, defaults.rollingWorkload.windowDays))),
    },
    assignmentSources: {
      draftPolicy: assignmentSources.draftPolicy === 'ignore' || assignmentSources.draftPolicy === 'committed' ? assignmentSources.draftPolicy : 'provisional',
      draftCertaintyMultiplier: Math.min(1, Math.max(0, numberOrDefault(assignmentSources.draftCertaintyMultiplier, defaults.assignmentSources.draftCertaintyMultiplier))),
      standbyPolicy: assignmentSources.standbyPolicy === 'ignore' || assignmentSources.standbyPolicy === 'treat_as_assignment' ? assignmentSources.standbyPolicy : 'context_only',
    },
    timing: {
      defaultMatchDurationMinutes: Math.max(30, numberOrDefault(timing.defaultMatchDurationMinutes, defaults.timing.defaultMatchDurationMinutes)),
      defaultTravelBufferMinutes: Math.max(0, numberOrDefault(timing.defaultTravelBufferMinutes, defaults.timing.defaultTravelBufferMinutes)),
    },
  };
}

export const advancingDefaultStrategy: RecommendationStrategyProfile = {
  name: 'Advancing League Default',
  advancementType: 'advancing',
  version: 1,
  competitivenessWeight: 45,
  participationFairnessWeight: 20,
  singlesOpportunityWeight: 10,
  scheduleSpacingWeight: 10,
  partnerCompatibilityWeight: 10,
  homeAwayBalanceWeight: 5,
  singlesEdrWeight: 70,
  singlesUtrWeight: 30,
  doublesEdrWeight: 70,
  doublesUtrWeight: 30,
  changeSensitivity: 'moderate',
  tentativePlayerPolicy: 'allow_in_draft_with_warning',
  preferredPairBonus: 20,
  preferredPairSeparationPenalty: -10,
  stronglyPreferredPairBonus: 40,
  stronglyPreferredPairSeparationPenalty: -30,
  avoidPairingPenalty: -20,
  poorCompatibilityPenalty: -10,
  goodCompatibilityBonus: 15,
  excellentCompatibilityBonus: 30,
  multiTeamScheduling: defaultMultiTeamSchedulingConfig,
  enforceTargetMatches: false,
  targetMatchVariance: 1,
  enforceMaxSit: false,
  maxWeeksWithoutMatch: 2,
  usePartnerCompatibility: true,
  usePartnerSchedulingPreferences: true,
};

export const nonAdvancingDefaultStrategy: RecommendationStrategyProfile = {
  name: 'Non-Advancing League Default',
  advancementType: 'non_advancing',
  version: 1,
  competitivenessWeight: 20,
  participationFairnessWeight: 30,
  singlesOpportunityWeight: 20,
  scheduleSpacingWeight: 10,
  partnerCompatibilityWeight: 15,
  homeAwayBalanceWeight: 5,
  singlesEdrWeight: 70,
  singlesUtrWeight: 30,
  doublesEdrWeight: 70,
  doublesUtrWeight: 30,
  changeSensitivity: 'moderate',
  tentativePlayerPolicy: 'allow_in_draft_with_warning',
  preferredPairBonus: 20,
  preferredPairSeparationPenalty: -10,
  stronglyPreferredPairBonus: 40,
  stronglyPreferredPairSeparationPenalty: -30,
  avoidPairingPenalty: -20,
  poorCompatibilityPenalty: -10,
  goodCompatibilityBonus: 15,
  excellentCompatibilityBonus: 30,
  multiTeamScheduling: defaultMultiTeamSchedulingConfig,
  enforceTargetMatches: false,
  targetMatchVariance: 1,
  enforceMaxSit: false,
  maxWeeksWithoutMatch: 2,
  usePartnerCompatibility: true,
  usePartnerSchedulingPreferences: true,
};

export function defaultStrategyForAdvancementType(advancementType: AdvancementType): RecommendationStrategyProfile {
  return advancementType === 'advancing' ? advancingDefaultStrategy : nonAdvancingDefaultStrategy;
}

export function validateStrategyProfile(profile: RecommendationStrategyProfile): StrategyValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const allWeights = [
    ...schedulingWeightKeys.map((key) => [key, Number(profile[key])] as const),
    ['singlesEdrWeight', profile.singlesEdrWeight] as const,
    ['singlesUtrWeight', profile.singlesUtrWeight] as const,
    ['doublesEdrWeight', profile.doublesEdrWeight] as const,
    ['doublesUtrWeight', profile.doublesUtrWeight] as const,
  ];
  allWeights.forEach(([key, value]) => {
    if (!Number.isFinite(value) || value < 0 || value > 100) errors.push(`${String(key)} must be between 0 and 100.`);
  });
  const schedulingTotal = schedulingWeightKeys.reduce((sum, key) => sum + Number(profile[key] || 0), 0);
  const singlesRatingTotal = Number(profile.singlesEdrWeight || 0) + Number(profile.singlesUtrWeight || 0);
  const doublesRatingTotal = Number(profile.doublesEdrWeight || 0) + Number(profile.doublesUtrWeight || 0);
  if (schedulingTotal !== 100) errors.push(`Scheduling Objectives Total is ${schedulingTotal}%.`);
  if (singlesRatingTotal !== 100) errors.push(`Singles rating total is ${singlesRatingTotal}%.`);
  if (doublesRatingTotal !== 100) errors.push(`Doubles rating total is ${doublesRatingTotal}%.`);
  if (profile.singlesUtrWeight > profile.singlesEdrWeight) warnings.push('Singles UTR is weighted more heavily than EDR in deprecated scoring fields.');
  if (profile.doublesUtrWeight > profile.doublesEdrWeight) warnings.push('Doubles UTR is weighted more heavily than EDR in deprecated scoring fields.');
  if (profile.participationFairnessWeight < 10) warnings.push('Deprecated participation fairness weight is below 10%; baseline fairness still remains active.');
  if (profile.enforceTargetMatches && (!Number.isInteger(Number(profile.targetMatchVariance ?? 1)) || Number(profile.targetMatchVariance ?? 1) < 0)) errors.push('Target match variance must be a non-negative whole number.');
  if (profile.enforceMaxSit && (!Number.isFinite(Number(profile.maxWeeksWithoutMatch)) || Number(profile.maxWeeksWithoutMatch) < 1 || Number(profile.maxWeeksWithoutMatch) > 12)) errors.push('Maximum weeks without a match must be between 1 and 12.');
  const scheduling = normalizeMultiTeamSchedulingConfig(profile.multiTeamScheduling);
  [
    ['Overlapping same-day warning penalty', scheduling.overlappingSameDay.warningPenalty],
    ['Same-day warning penalty', scheduling.nonOverlappingSameDay.warningPenalty],
    ['Consecutive-day warning penalty', scheduling.consecutiveDay.warningPenalty],
    ['Consecutive-day away warning penalty', scheduling.consecutiveDayAway.warningPenalty],
    ['Consecutive-day singles warning penalty', scheduling.consecutiveDaySingles.warningPenalty],
    ['Recovery warning penalty', scheduling.recovery.warningPenalty],
    ['Rolling workload warning penalty', scheduling.rollingWorkload.warningPenalty],
  ].forEach(([label, value]) => {
    if (!Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 100) errors.push(`${label} must be between 0 and 100.`);
  });
  if (scheduling.recovery.minimumHours < 0 || scheduling.recovery.minimumHours > 168) errors.push('Minimum recovery hours must be between 0 and 168.');
  if (scheduling.rollingWorkload.maxAssignments < 0 || scheduling.rollingWorkload.maxAssignments > 30) errors.push('Rolling workload maximum assignments must be between 0 and 30.');
  if (scheduling.rollingWorkload.windowDays < 1 || scheduling.rollingWorkload.windowDays > 90) errors.push('Rolling workload window days must be between 1 and 90.');
  if (scheduling.timing.defaultMatchDurationMinutes < 30 || scheduling.timing.defaultMatchDurationMinutes > 600) errors.push('Default match duration must be between 30 and 600 minutes.');
  if (scheduling.timing.defaultTravelBufferMinutes < 0 || scheduling.timing.defaultTravelBufferMinutes > 360) errors.push('Default travel buffer must be between 0 and 360 minutes.');
  return { valid: errors.length === 0, errors, warnings, schedulingTotal, singlesRatingTotal, doublesRatingTotal };
}

function normalizeRatings(players: RecommendationPlayer[], selector: (player: RecommendationPlayer) => number | null): Map<string, number | null> {
  const values = players.map(selector).filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (values.length === 0) return new Map(players.map((player) => [player.id, null]));
  const min = Math.min(...values);
  const max = Math.max(...values);
  return new Map(players.map((player) => {
    const value = selector(player);
    if (typeof value !== 'number' || !Number.isFinite(value)) return [player.id, null];
    if (min === max) return [player.id, 75];
    return [player.id, Math.round(((value - min) / (max - min)) * 1000) / 10];
  }));
}

function normalizedTier(playerTier: string | null): 'tier_1' | 'tier_2' | 'tier_3' | null {
  if (playerTier === 'tier_1' || playerTier === 'tier_2' || playerTier === 'tier_3') return playerTier;
  if (playerTier === 'tier_4') return 'tier_3';
  return null;
}

function tierFallbackScore(playerTier: string | null): number {
  const tier = normalizedTier(playerTier);
  if (tier === 'tier_1') return 85;
  if (tier === 'tier_2') return 65;
  if (tier === 'tier_3') return 45;
  return 40;
}

export function calculatePlayerStrength(
  player: RecommendationPlayer,
  assignmentType: 'singles' | 'doubles',
  roster: RecommendationPlayer[],
  strategy: RecommendationStrategyProfile,
  ratingBasis: PlayerRatingBasis = 'edr',
): RatingExplanation {
  const edrScores = normalizeRatings(roster, (item) => item.estimatedDynamicRating);
  const utrScores = normalizeRatings(roster, (item) => assignmentType === 'singles' ? item.utrSinglesRating : item.utrDoublesRating);
  const edr = edrScores.get(player.id) ?? null;
  const utr = utrScores.get(player.id) ?? null;
  const edrWeight = assignmentType === 'singles' ? strategy.singlesEdrWeight : strategy.doublesEdrWeight;
  const utrWeight = assignmentType === 'singles' ? strategy.singlesUtrWeight : strategy.doublesUtrWeight;
  const utrName = assignmentType === 'singles' ? 'Singles UTR' : 'Doubles UTR';

  if (ratingBasis === 'tier') {
    return {
      playerId: player.id,
      assignmentType,
      score: tierFallbackScore(player.playerTier),
      confidence: player.playerTier ? 'Moderate' : 'Low',
      text: player.playerTier ? 'Roster tier is the team primary ranking basis. Confidence: Moderate.' : `No roster tier is assigned; neutral tier fallback was used. Confidence: Low.`,
    };
  }

  if (ratingBasis === 'utr' && utr !== null) {
    return {
      playerId: player.id,
      assignmentType,
      score: utr,
      confidence: 'High',
      text: `${utrName} is the team primary ranking basis. Confidence: High.`,
    };
  }
  if (ratingBasis === 'utr' && edr !== null) {
    return {
      playerId: player.id,
      assignmentType,
      score: edr,
      confidence: 'Reduced',
      text: `${utrName} is unavailable, so normalized EDR is used as the secondary ranking signal. Confidence: Reduced.`,
    };
  }

  if (edr !== null && utr !== null) {
    const score = (edr * edrWeight + utr * utrWeight) / 100;
    return {
      playerId: player.id,
      assignmentType,
      score,
      confidence: 'High',
      text: `Uses ${edrWeight}% EDR and ${utrWeight}% ${utrName}; both ratings are available. Confidence: High.`,
    };
  }
  if (edr !== null) {
    return {
      playerId: player.id,
      assignmentType,
      score: edr,
      confidence: 'Moderate',
      text: `${utrName} is unavailable, so normalized EDR is renormalized to 100%. Configured weights were ${edrWeight}% EDR and ${utrWeight}% ${utrName}. Confidence: Moderate.`,
    };
  }
  if (utr !== null) {
    return {
      playerId: player.id,
      assignmentType,
      score: utr,
      confidence: 'Reduced',
      text: `EDR is unavailable, so normalized ${utrName} is renormalized to 100%. Configured weights were ${edrWeight}% EDR and ${utrWeight}% ${utrName}. Confidence: Reduced.`,
    };
  }
  return {
    playerId: player.id,
    assignmentType,
    score: tierFallbackScore(player.playerTier),
    confidence: 'Low',
    text: `No EDR or ${utrName} is available; player tier fallback was used. Confidence: Low.`,
  };
}

function compatibilityAdjustment(compatibility: PartnerCompatibility, strategy: RecommendationStrategyProfile): number {
  if (strategy.usePartnerCompatibility === false) return 0;
  if (compatibility === 'Excellent') return strategy.excellentCompatibilityBonus ?? 30;
  if (compatibility === 'Good') return strategy.goodCompatibilityBonus ?? 15;
  if (compatibility === 'Poor') return strategy.poorCompatibilityPenalty ?? -10;
  return 0;
}

function pairingRulePairedBonus(rule: PartnerPairingRule, strategy: RecommendationStrategyProfile): number {
  if (strategy.usePartnerSchedulingPreferences === false) return 0;
  if (rule === 'preferred') return strategy.preferredPairBonus ?? 20;
  if (rule === 'strongly_preferred') return strategy.stronglyPreferredPairBonus ?? 40;
  if (rule === 'avoid_pairing') return strategy.avoidPairingPenalty ?? -20;
  return 0;
}

function hardPartnerRuleForPlayer(input: RecommendationInput, playerId: string, rule: PartnerPairingRule): string | null {
  if (input.strategy.usePartnerSchedulingPreferences === false) return null;
  const preference = input.partnerPreferences.find((item) => item.is_active !== false && normalizePairingRule(item.pairing_rule) === rule && (item.player1_id === playerId || item.player2_id === playerId));
  if (!preference) return null;
  return preference.player1_id === playerId ? preference.player2_id : preference.player1_id;
}

function hasExcludedPair(input: RecommendationInput, playerAId: string, playerBId: string): boolean {
  if (input.strategy.usePartnerSchedulingPreferences === false) return false;
  const preference = input.workspaceId ? partnerPreferenceForPair(input.partnerPreferences, input.workspaceId, playerAId, playerBId) : null;
  return preference?.is_active !== false && normalizePairingRule(preference?.pairing_rule) === 'excluded';
}

export function calculateDoublesPairStrength(
  playerA: RecommendationPlayer,
  playerB: RecommendationPlayer,
  roster: RecommendationPlayer[],
  strategy: RecommendationStrategyProfile,
  compatibility: PartnerCompatibility,
  ratingBasis: PlayerRatingBasis = 'edr',
): { score: number; explanations: RatingExplanation[]; compatibilityAdjustment: number } {
  const playerAStrength = calculatePlayerStrength(playerA, 'doubles', roster, strategy, ratingBasis);
  const playerBStrength = calculatePlayerStrength(playerB, 'doubles', roster, strategy, ratingBasis);
  const adjustment = compatibilityAdjustment(compatibility, strategy);
  return {
    score: (playerAStrength.score + playerBStrength.score) / 2 + adjustment,
    explanations: [playerAStrength, playerBStrength],
    compatibilityAdjustment: adjustment,
  };
}

function projectedTotal(player: RecommendationPlayer): number {
  return player.completedPlayed + player.futureSingles + player.futureDoubles;
}

function fairnessScore(player: RecommendationPlayer): number {
  const target = Math.max(0, player.targetMatches || 0);
  if (target === 0) return 50;
  const deficit = target - projectedTotal(player);
  if (deficit > 0) return Math.min(100, 55 + deficit * 15);
  if (deficit < 0) return Math.max(0, 50 + deficit * 10);
  return 55;
}

function baselineParticipationScore(player: RecommendationPlayer): number {
  const target = Math.max(0, player.targetMatches || 0);
  const total = projectedTotal(player);
  if (target === 0) return Math.max(20, 70 - total * 6);
  const pace = target - total;
  if (pace > 0) return Math.min(100, 60 + pace * 12);
  if (pace < 0) return Math.max(15, 55 + pace * 9);
  return 58;
}

function maxSitScore(input: RecommendationInput, player: RecommendationPlayer): number {
  if (!input.strategy.enforceMaxSit) return 0;
  const maxWeeks = Number(input.strategy.maxWeeksWithoutMatch || 0);
  if (!Number.isFinite(maxWeeks) || maxWeeks <= 0) return 0;
  const thresholdDays = maxWeeks * 7;
  if (player.lastPlayedDate) {
    const referenceDate = Date.parse(`${input.match.matchDate}T12:00:00Z`);
    const elapsedDays = Math.max(0, Math.round((referenceDate - Date.parse(`${player.lastPlayedDate}T12:00:00Z`)) / 86_400_000));
    if (elapsedDays >= thresholdDays) return 100;
    if (elapsedDays >= Math.max(0, thresholdDays - 3)) return 85;
    return Math.max(0, 40 + (elapsedDays / thresholdDays) * 35);
  }
  // Older callers may only have a rounded week value. Preserve their behavior
  // until they can provide an actual completed match date.
  const weeksSince = player.lastPlayedWeeksAgo;
  if (weeksSince == null) return 55;
  if (weeksSince >= maxWeeks) return 100;
  if (weeksSince === maxWeeks - 1) return 85;
  return Math.max(0, 40 + (weeksSince / maxWeeks) * 35);
}

function participationScore(input: RecommendationInput, player: RecommendationPlayer): number {
  const targetScore = Math.max(fairnessScore(player), baselineParticipationScore(player));
  const bestEffortSitBonus = maxSitScore(input, player) * 0.08;
  return Math.min(100, targetScore + bestEffortSitBonus);
}

function remainingTargetMatches(player: RecommendationPlayer): number {
  return Math.max(0, (player.targetMatches || 0) - projectedTotal(player));
}

function targetFloorPressure(player: RecommendationPlayer): number {
  const remainingTarget = remainingTargetMatches(player);
  if (remainingTarget <= 0) return 0;
  const remainingOpportunities = Math.max(1, player.seasonRemainingOpportunities ?? remainingTarget);
  return remainingTarget / remainingOpportunities;
}

function targetConstraintEnabled(input: RecommendationInput): boolean {
  return (input.respectTargetMatchCounts === true || input.strategy.enforceTargetMatches === true) && input.players.some((player) => (player.targetMatches || 0) > 0);
}

function targetVariance(input: RecommendationInput): number {
  if (!targetConstraintEnabled(input)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor(Number(input.strategy.targetMatchVariance ?? 1) || 0));
}

function exactTargetConstraintEnabled(input: RecommendationInput): boolean {
  return targetConstraintEnabled(input) && targetVariance(input) === 0;
}

function targetEligible(input: RecommendationInput, player: RecommendationPlayer): boolean {
  if (!targetConstraintEnabled(input)) return true;
  const target = Math.max(0, player.targetMatches || 0);
  if (target === 0) return true;
  return projectedTotal(player) < target + targetVariance(input);
}

function opponentDifficultyScore(match: RecommendationMatch): number {
  if (match.opponentRatingBasis === 'subjective' && typeof match.opponentSubjectiveDifficulty === 'number') {
    return Math.min(5, Math.max(1, match.opponentSubjectiveDifficulty));
  }
  const edr = match.opponentAverageEdr;
  if (typeof edr !== 'number' || !Number.isFinite(edr)) return 3;
  if (edr < 2.7) return 1;
  if (edr < 3.0) return 2;
  if (edr < 3.3) return 3;
  if (edr < 3.6) return 4;
  return 5;
}

function tierDifficultyTarget(match: RecommendationMatch): number {
  const difficulty = opponentDifficultyScore(match);
  if (difficulty <= 2) return 45;
  if (difficulty >= 4) return 85;
  return 65;
}

function strengthFitScore(input: RecommendationInput, strengthScore: number): number {
  if ((input.playerRatingBasis || 'edr') === 'tier') {
    const target = tierDifficultyTarget(input.match);
    return Math.max(0, 100 - Math.abs(strengthScore - target) * 1.4);
  }
  if (input.match.opponentRatingBasis === 'avg_edr' && input.match.opponentAverageEdr === null) {
    return Math.max(0, strengthScore - 5);
  }
  return strengthScore;
}

function lineStrengthPriority(input: RecommendationInput, position: RecommendationPosition): number {
  const ordered = [...input.positions].sort((a, b) => a.displayOrder - b.displayOrder || a.positionCode.localeCompare(b.positionCode));
  const index = Math.max(0, ordered.findIndex((item) => item.id === position.id));
  const linePressure = ordered.length <= 1 ? 1 : (ordered.length - index) / ordered.length;
  return linePressure * (0.7 + (opponentDifficultyScore(input.match) / 5) * 0.6);
}

function shouldPrioritizeStrengthForMatch(input: RecommendationInput): boolean {
  return (input.playerRatingBasis || 'edr') === 'tier' && opponentDifficultyScore(input.match) >= 4;
}

function shouldPrioritizeStrengthForLine(input: RecommendationInput, position: RecommendationPosition): boolean {
  return lineStrengthPriority(input, position) >= 0.55;
}

function availabilityPreference(player: RecommendationPlayer): number {
  if (player.availabilityStatus === 'available') return 0;
  if (player.availabilityStatus === 'maybe') return 1;
  return 2;
}

function compareTargetNeedThenScore(
  input: RecommendationInput,
  position: RecommendationPosition,
  a: { player: RecommendationPlayer; score: number },
  b: { player: RecommendationPlayer; score: number },
): number {
  const strengthFirst = !exactTargetConstraintEnabled(input) && (shouldPrioritizeStrengthForMatch(input) || shouldPrioritizeStrengthForLine(input, position));
  const targetNeedDifference = targetConstraintEnabled(input)
    ? remainingTargetMatches(b.player) - remainingTargetMatches(a.player)
    : 0;
  if (strengthFirst) {
    const scoreDifference = b.score - a.score;
    if (Math.abs(scoreDifference) > 0.001) return scoreDifference;
  }
  if (targetConstraintEnabled(input)) {
    if (exactTargetConstraintEnabled(input)) {
      const pressureDifference = targetFloorPressure(b.player) - targetFloorPressure(a.player);
      if (Math.abs(pressureDifference) > 0.001) return pressureDifference;
    }
    if (targetNeedDifference !== 0) return targetNeedDifference;
  }
  const availabilityDifference = availabilityPreference(a.player) - availabilityPreference(b.player);
  if (availabilityDifference !== 0) return availabilityDifference;
  return b.score - a.score || playerSortKey(a.player).localeCompare(playerSortKey(b.player));
}

function singlesOpportunityScore(player: RecommendationPlayer, preference: SinglesPreference, hasSinglesLines: boolean): number {
  if (!hasSinglesLines || preference === 'no') return 0;
  if (player.singlesPlayed > 0 || player.futureSingles > 0) return preference === 'yes' ? 20 : 8;
  return preference === 'yes' ? 100 : 45;
}

function homeAwayScore(player: RecommendationPlayer, matchSiteType: MatchSiteType): number {
  const total = player.totalAssignmentsForBalance ?? (player.homeMatches || 0) + (player.awayMatches || 0) + (player.neutralMatches || 0);
  if (total === 0) return 50;
  const homePct = ((player.homeMatches || 0) / total) * 100;
  if (matchSiteType === 'Home') return homePct < 50 ? 70 : 35;
  if (matchSiteType === 'Away') return homePct > 50 ? 70 : 35;
  return 50;
}

type SchedulingPolicyEvaluation = {
  blocked: boolean;
  warnings: string[];
  penalty: number;
  score: number;
};

function playerSortKey(player: RecommendationPlayer): string {
  return `${player.lastName}|${player.firstName}|${player.id}`;
}

function dateTimeFromParts(date: string | null | undefined, time: string | null | undefined): Date | null {
  if (!date) return null;
  const normalizedTime = (time || '00:00:00').slice(0, 8);
  const parsed = new Date(`${date}T${normalizedTime}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function assignmentArrivalDateTime(assignment: Pick<SharedPlayerScheduleAssignment, 'matchDate' | 'matchTime' | 'arrivalDateTime'>): Date | null {
  if (assignment.arrivalDateTime) {
    const parsed = new Date(assignment.arrivalDateTime);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return dateTimeFromParts(assignment.matchDate, assignment.matchTime);
}

function assignmentWindow(
  assignment: Pick<SharedPlayerScheduleAssignment, 'matchDate' | 'matchTime' | 'arrivalDateTime'>,
  config: MultiTeamSchedulingConfig,
): { start: Date | null; end: Date | null } {
  const arrival = assignmentArrivalDateTime(assignment);
  if (!arrival) return { start: null, end: null };
  return {
    start: arrival,
    end: new Date(arrival.getTime() + config.timing.defaultMatchDurationMinutes * 60_000),
  };
}

function daysBetween(a: string, b: string): number | null {
  const dateA = dateTimeFromParts(a, '00:00:00');
  const dateB = dateTimeFromParts(b, '00:00:00');
  if (!dateA || !dateB) return null;
  return Math.round((dateA.getTime() - dateB.getTime()) / 86_400_000);
}

function windowsOverlap(
  candidate: { start: Date | null; end: Date | null },
  existing: { start: Date | null; end: Date | null },
  travelBufferMinutes: number,
): boolean {
  if (!candidate.start || !candidate.end || !existing.start || !existing.end) return false;
  const buffer = travelBufferMinutes * 60_000;
  return candidate.start.getTime() - buffer < existing.end.getTime() && existing.start.getTime() - buffer < candidate.end.getTime();
}

function formatAssignmentLabel(assignment: SharedPlayerScheduleAssignment): string {
  const line = assignment.positionCode ? ` ${assignment.positionCode}` : '';
  return `${assignment.teamName || 'another team'} vs ${assignment.opponentName || 'TBD'} on ${assignment.matchDate}${line}`;
}

function applySchedulingPolicy(
  result: SchedulingPolicyEvaluation,
  policy: SchedulingPolicyConfig,
  message: string,
  certaintyMultiplier: number,
) {
  if (policy.policy === 'allow') return;
  if (policy.policy === 'block') {
    result.blocked = true;
    result.warnings.push(message);
    return;
  }
  result.penalty += Math.max(0, policy.warningPenalty) * certaintyMultiplier;
  result.warnings.push(message);
}

function assignmentCertaintyMultiplier(assignment: SharedPlayerScheduleAssignment, config: MultiTeamSchedulingConfig): number {
  if (assignment.lineupStatus === 'Draft' || assignment.certainty === 'provisional') return config.assignmentSources.draftCertaintyMultiplier;
  if (assignment.certainty === 'context') return 0.25;
  return 1;
}

function normalizeBackToBackTolerance(value: RecommendationPlayer['backToBackTolerance']): 'no' | 'if_needed' | 'yes' {
  return value === 'no' || value === 'yes' ? value : 'if_needed';
}

function teamPriorityValue(value: SharedPlayerScheduleAssignment['teamPriority'] | RecommendationPlayer['teamPriority']): number {
  if (value === 'primary') return 3;
  if (value === 'secondary') return 1;
  return 2;
}

function crossTeamPriorityMultiplier(
  candidatePriority: RecommendationPlayer['teamPriority'],
  existingPriority: SharedPlayerScheduleAssignment['teamPriority'],
): number {
  const difference = teamPriorityValue(candidatePriority) - teamPriorityValue(existingPriority);
  if (difference <= -2) return 1.35;
  if (difference < 0) return 1.15;
  if (difference >= 2) return 0.7;
  if (difference > 0) return 0.85;
  return 1;
}

function teamPriorityContext(
  candidatePriority: RecommendationPlayer['teamPriority'],
  existingPriority: SharedPlayerScheduleAssignment['teamPriority'],
): string {
  const candidate = candidatePriority || 'normal';
  const existing = existingPriority || 'normal';
  if (candidate === existing) return '';
  return ` This team is ${candidate} priority and the other team is ${existing} priority.`;
}

function qualifyingScheduleAssignmentsForPlayer(input: RecommendationInput, playerId: string): SharedPlayerScheduleAssignment[] {
  const config = normalizeMultiTeamSchedulingConfig(input.strategy.multiTeamScheduling);
  const policyTeamIds = input.crossTeamPolicyTeamIds?.length ? new Set(input.crossTeamPolicyTeamIds) : null;
  if (!policyTeamIds?.has(input.match.teamId)) return [];
  return (input.sharedPlayerSchedule || [])
    .filter((assignment) => assignment.playerId === playerId)
    .filter((assignment) => assignment.matchId !== input.match.id)
    .filter((assignment) => policyTeamIds.has(assignment.teamId))
    .filter((assignment) => assignment.matchStatus !== 'Cancelled')
    .filter((assignment) => {
      if (assignment.source === 'standby') return config.assignmentSources.standbyPolicy !== 'ignore';
      if (assignment.lineupStatus === 'Draft') return config.assignmentSources.draftPolicy !== 'ignore';
      return assignment.lineupStatus === 'Published' || assignment.matchStatus === 'Completed';
    })
    .map((assignment) => {
      if (assignment.source === 'standby' && config.assignmentSources.standbyPolicy === 'context_only') return { ...assignment, certainty: 'context' as const };
      if (assignment.lineupStatus === 'Draft' && config.assignmentSources.draftPolicy === 'provisional') return { ...assignment, certainty: 'provisional' as const };
      return assignment;
    });
}

function evaluatePlayerSchedulingPolicies(input: RecommendationInput, player: RecommendationPlayer, position: RecommendationPosition): SchedulingPolicyEvaluation {
  const config = normalizeMultiTeamSchedulingConfig(input.strategy.multiTeamScheduling);
  const candidate: SharedPlayerScheduleAssignment = {
    playerId: player.id,
    matchId: input.match.id,
    teamId: input.match.teamId,
    opponentName: input.match.opponentName,
    matchDate: input.match.matchDate,
    matchTime: input.match.matchTime || null,
    arrivalDateTime: input.match.arrivalDateTime || null,
    matchSiteType: input.match.matchSiteType,
    positionCode: position.positionCode,
    assignmentType: position.lineType === 'singles' ? 'singles' : 'doubles',
    lineupStatus: input.match.lineupStatus,
    matchStatus: input.match.matchStatus,
    certainty: 'committed',
    source: 'lineup',
  };
  const result: SchedulingPolicyEvaluation = { blocked: false, warnings: [], penalty: 0, score: 70 };
  const candidateWindow = assignmentWindow(candidate, config);
  const existingAssignments = qualifyingScheduleAssignmentsForPlayer(input, player.id);
  const rollingStart = dateTimeFromParts(candidate.matchDate, '00:00:00');
  const rollingEnd = rollingStart ? new Date(rollingStart.getTime() + (config.rollingWorkload.windowDays - 1) * 86_400_000) : null;
  let rollingCount = 1;

  for (const existing of existingAssignments) {
    const certaintyMultiplier = assignmentCertaintyMultiplier(existing, config);
    const existingWindow = assignmentWindow(existing, config);
    const dayDelta = daysBetween(candidate.matchDate, existing.matchDate);
    if (candidate.matchDate === existing.matchDate) {
      if (windowsOverlap(candidateWindow, existingWindow, config.timing.defaultTravelBufferMinutes)) {
        applySchedulingPolicy(result, config.overlappingSameDay, `${playerName(player)} has an overlapping same-day assignment: ${formatAssignmentLabel(existing)}.`, certaintyMultiplier);
      } else {
        const tolerance = normalizeBackToBackTolerance(player.sameDayTolerance);
        if (tolerance !== 'yes') applySchedulingPolicy(result, config.nonOverlappingSameDay, `${playerName(player)} has another same-day assignment: ${formatAssignmentLabel(existing)}. Same-day tolerance is ${tolerance.replace(/_/g, ' ')}.`, certaintyMultiplier * (tolerance === 'no' ? 1.6 : 1));
      }
    }
    if (dayDelta !== null && Math.abs(dayDelta) === 1) {
      const tolerance = normalizeBackToBackTolerance(player.backToBackTolerance);
      // A "Yes" preference makes ordinary consecutive dates acceptable. Same-day,
      // overlap, recovery, and dense-workload policies still apply independently.
      if (tolerance !== 'yes') {
        const toleranceMultiplier = tolerance === 'no' ? 1.6 : 1;
        const priorityMultiplier = crossTeamPriorityMultiplier(player.teamPriority, existing.teamPriority);
        const preferenceMultiplier = certaintyMultiplier * toleranceMultiplier * priorityMultiplier;
        const context = teamPriorityContext(player.teamPriority, existing.teamPriority);
        applySchedulingPolicy(result, config.consecutiveDay, `${playerName(player)} has a consecutive-day assignment: ${formatAssignmentLabel(existing)}. Back-to-back tolerance is ${tolerance.replace(/_/g, ' ')}.${context}`, preferenceMultiplier);
        if (candidate.matchSiteType === 'Away' && existing.matchSiteType === 'Away') {
          applySchedulingPolicy(result, config.consecutiveDayAway, `${playerName(player)} has consecutive-day away assignments.${context}`, preferenceMultiplier);
        }
        const bothSingles = candidate.assignmentType === 'singles' && existing.assignmentType === 'singles';
        const eitherSingles = candidate.assignmentType === 'singles' || existing.assignmentType === 'singles';
        if ((config.consecutiveDaySingles.mode === 'both_singles' && bothSingles) || (config.consecutiveDaySingles.mode === 'either_singles' && eitherSingles)) {
          applySchedulingPolicy(result, config.consecutiveDaySingles, `${playerName(player)} has compressed singles workload on adjacent days.${context}`, preferenceMultiplier);
        }
      }
    }
    if (candidateWindow.start && candidateWindow.end && existingWindow.start && existingWindow.end) {
      const first = candidateWindow.start < existingWindow.start ? candidateWindow : existingWindow;
      const second = candidateWindow.start < existingWindow.start ? existingWindow : candidateWindow;
      const recoveryHours = (second.start!.getTime() - first.end!.getTime()) / 3_600_000;
      const preferredRecoveryHours = Number.isFinite(Number(player.minimumRecoveryHours)) ? Math.max(0, Number(player.minimumRecoveryHours)) : config.recovery.minimumHours;
      if (recoveryHours >= 0 && recoveryHours < preferredRecoveryHours) {
        applySchedulingPolicy(result, config.recovery, `${playerName(player)} has only ${recoveryHours.toFixed(1)} hours recovery before/after ${formatAssignmentLabel(existing)}; preference is ${preferredRecoveryHours} hours.`, certaintyMultiplier);
      }
    }
    if (rollingStart && rollingEnd) {
      const existingDate = dateTimeFromParts(existing.matchDate, '00:00:00');
      if (existingDate && existingDate >= rollingStart && existingDate <= rollingEnd) rollingCount += 1;
    }
  }
  if (rollingCount > config.rollingWorkload.maxAssignments) {
    applySchedulingPolicy(result, config.rollingWorkload, `${playerName(player)} would have ${rollingCount} assignments in ${config.rollingWorkload.windowDays} days.`, 1);
  }
  result.score = Math.max(0, 80 - result.penalty);
  return result;
}

function eligiblePlayersForPosition(input: RecommendationInput, position: RecommendationPosition, usedPlayerIds: Set<string>, planType: RecommendationPlanType) {
  const hasSinglesLines = input.positions.some((item) => item.lineType === 'singles');
  return input.players
    .filter((player) => {
      if (usedPlayerIds.has(player.id)) return false;
      if (isDesignatedSubstitute({ roster_role: player.rosterRole })) return false;
      if (player.availabilityStatus === 'not_available') return false;
      if (player.availabilityStatus === 'maybe' && input.strategy.tentativePlayerPolicy === 'exclude_tentative') return false;
      if (player.availabilityStatus === 'maybe' && input.strategy.tentativePlayerPolicy === 'standby_only') return false;
      if (input.excludedPlayerMatchPairs?.some((item) => item.playerId === player.id && item.matchId === input.match.id)) return false;
      if (position.lineType === 'singles' && (input.singlesPreferenceByPlayerId?.[player.id] || 'if_possible') === 'no') return false;
      if (evaluatePlayerSchedulingPolicies(input, player, position).blocked) return false;
      if (position.requiredMaleCount > 0 || position.requiredFemaleCount > 0) return true;
      if (hasSinglesLines && planType !== 'minimal_disruption') return true;
      return true;
    })
    .sort((a, b) => playerSortKey(a).localeCompare(playerSortKey(b)));
}

function scorePlayerForPosition(input: RecommendationInput, player: RecommendationPlayer, position: RecommendationPosition, planType: RecommendationPlanType): { score: number; explanation: RatingExplanation; policyEvaluation: SchedulingPolicyEvaluation } {
  const assignmentType = position.lineType === 'singles' ? 'singles' : 'doubles';
  const strength = calculatePlayerStrength(player, assignmentType, input.players, input.strategy, input.playerRatingBasis || 'edr');
  const preference = input.singlesPreferenceByPlayerId?.[player.id] || 'if_possible';
  const policyEvaluation = evaluatePlayerSchedulingPolicies(input, player, position);
  const competitiveness = strengthFitScore(input, strength.score);
  const participation = participationScore(input, player);
  const objective = {
    competitiveness,
    participationFairness: participation,
    singlesOpportunity: position.lineType === 'singles' ? singlesOpportunityScore(player, preference, true) : 0,
    scheduleSpacing: Math.min(player.availabilityStatus === 'maybe' ? 35 : 60, policyEvaluation.score),
    partnerCompatibility: 50,
    homeAwayBalance: homeAwayScore(player, input.match.matchSiteType),
  };
  if (planType === 'strongest_competitive') return { score: objective.competitiveness * 1.5 + objective.scheduleSpacing * 0.1 - policyEvaluation.penalty, explanation: strength, policyEvaluation };
  if (planType === 'minimal_disruption') return { score: objective.competitiveness * 0.5 + objective.participationFairness * 0.35 + objective.scheduleSpacing * 0.15 - policyEvaluation.penalty, explanation: strength, policyEvaluation };
  const score =
    objective.competitiveness * 0.52 +
    objective.participationFairness * 0.28 +
    objective.singlesOpportunity * 0.08 +
    objective.scheduleSpacing * 0.08 +
    objective.homeAwayBalance * 0.04 -
    policyEvaluation.penalty;
  return { score, explanation: strength, policyEvaluation };
}

function choosePlayersForPosition(input: RecommendationInput, position: RecommendationPosition, usedPlayerIds: Set<string>, planType: RecommendationPlanType) {
  const candidates = eligiblePlayersForPosition(input, position, usedPlayerIds, planType);
  if (position.requiredPlayerCount === 1) {
    const constrainedCandidates = targetConstraintEnabled(input)
      ? candidates.filter((player) => remainingTargetMatches(player) > 0 || targetEligible(input, player))
      : [];
    const ranked = (constrainedCandidates.length > 0 ? constrainedCandidates : candidates)
      .map((player) => ({ player, ...scorePlayerForPosition(input, player, position, planType) }))
      .sort((a, b) => compareTargetNeedThenScore(input, position, a, b));
    return ranked.slice(0, 1);
  }

  const pairs: Array<{ players: RecommendationPlayer[]; score: number; explanations: RatingExplanation[]; compatibility: PartnerCompatibility; pairingRule: PartnerPairingRule; policyEvaluations: SchedulingPolicyEvaluation[] }> = [];
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      const playerA = candidates[i];
      const playerB = candidates[j];
      if (position.requiredMaleCount > 0 && [playerA, playerB].filter((player) => player.sex === 'male').length !== position.requiredMaleCount) continue;
      if (position.requiredFemaleCount > 0 && [playerA, playerB].filter((player) => player.sex === 'female').length !== position.requiredFemaleCount) continue;
      const preference = input.workspaceId ? partnerPreferenceForPair(input.partnerPreferences, input.workspaceId, playerA.id, playerB.id) : null;
      const compatibility = normalizeCompatibility(preference?.compatibility);
      const pairingRule = normalizePairingRule(preference?.pairing_rule);
      if (pairingRule === 'excluded') continue;
      const pairStrength = calculateDoublesPairStrength(playerA, playerB, input.players, input.strategy, compatibility, input.playerRatingBasis || 'edr');
      const strictPartnerA = hardPartnerRuleForPlayer(input, playerA.id, 'schedule_only_as_pair');
      const strictPartnerB = hardPartnerRuleForPlayer(input, playerB.id, 'schedule_only_as_pair');
      if ((strictPartnerA && strictPartnerA !== playerB.id) || (strictPartnerB && strictPartnerB !== playerA.id)) continue;
      if (strictPartnerA === playerB.id && strictPartnerB && strictPartnerB !== playerA.id) continue;
      if (strictPartnerB === playerA.id && strictPartnerA && strictPartnerA !== playerB.id) continue;
      if (hasExcludedPair(input, playerA.id, playerB.id)) continue;
      const policyEvaluations = [evaluatePlayerSchedulingPolicies(input, playerA, position), evaluatePlayerSchedulingPolicies(input, playerB, position)];
      if (policyEvaluations.some((evaluation) => evaluation.blocked)) continue;
      const fairness = (
        participationScore(input, playerA)
        + participationScore(input, playerB)
      ) / 2;
      const homeAway = (homeAwayScore(playerA, input.match.matchSiteType) + homeAwayScore(playerB, input.match.matchSiteType)) / 2;
      const schedulingScore = (policyEvaluations[0].score + policyEvaluations[1].score) / 2;
      const policyPenalty = policyEvaluations.reduce((sum, evaluation) => sum + evaluation.penalty, 0);
      const pairRuleBonus = pairingRulePairedBonus(pairingRule, input.strategy);
      const compatibilityScore = 50 + pairStrength.compatibilityAdjustment + pairRuleBonus;
      const competitiveFit = strengthFitScore(input, pairStrength.score);
      const score = planType === 'strongest_competitive'
        ? pairStrength.score * 1.5 + pairRuleBonus - policyPenalty
        : planType === 'minimal_disruption'
          ? competitiveFit * 0.55 + fairness * 0.25 + compatibilityScore * 0.1 + schedulingScore * 0.1 - policyPenalty
          : competitiveFit * 0.52
            + fairness * 0.28
            + schedulingScore * 0.08
            + compatibilityScore * 0.08
            + homeAway * 0.04
            - policyPenalty;
      pairs.push({ players: [playerA, playerB], score, explanations: pairStrength.explanations, compatibility, pairingRule, policyEvaluations });
    }
  }
  const targetAwarePairs = targetConstraintEnabled(input)
    ? pairs.filter((pair) => pair.players.every((player) => remainingTargetMatches(player) > 0 || targetEligible(input, player)))
    : [];
  const oneNeededPlayerPairs = targetConstraintEnabled(input) && targetAwarePairs.length === 0
    ? pairs.filter((pair) => pair.players.some((player) => remainingTargetMatches(player) > 0))
    : [];
  const selectablePairs = targetAwarePairs.length > 0 ? targetAwarePairs : oneNeededPlayerPairs.length > 0 ? oneNeededPlayerPairs : pairs;
  selectablePairs.sort((a, b) => {
    const strengthFirst = !exactTargetConstraintEnabled(input) && (shouldPrioritizeStrengthForMatch(input) || shouldPrioritizeStrengthForLine(input, position));
    const targetNeedDifference = targetConstraintEnabled(input)
      ? b.players.reduce((sum, player) => sum + remainingTargetMatches(player), 0) - a.players.reduce((sum, player) => sum + remainingTargetMatches(player), 0)
      : 0;
    const lowestNeedDifference = targetConstraintEnabled(input)
      ? Math.min(...b.players.map(remainingTargetMatches)) - Math.min(...a.players.map(remainingTargetMatches))
      : 0;
    if (strengthFirst) {
      const scoreDifference = b.score - a.score;
      if (Math.abs(scoreDifference) > 0.001) return scoreDifference;
    }
    if (targetConstraintEnabled(input)) {
      if (exactTargetConstraintEnabled(input)) {
        const pressureDifference = b.players.reduce((sum, player) => sum + targetFloorPressure(player), 0) - a.players.reduce((sum, player) => sum + targetFloorPressure(player), 0);
        if (Math.abs(pressureDifference) > 0.001) return pressureDifference;
      }
      if (targetNeedDifference !== 0) return targetNeedDifference;
      if (lowestNeedDifference !== 0) return lowestNeedDifference;
    }
    const availabilityDifference = a.players.reduce((sum, player) => sum + availabilityPreference(player), 0) - b.players.reduce((sum, player) => sum + availabilityPreference(player), 0);
    if (availabilityDifference !== 0) return availabilityDifference;
    return b.score - a.score || playerSortKey(a.players[0]).localeCompare(playerSortKey(b.players[0])) || playerSortKey(a.players[1]).localeCompare(playerSortKey(b.players[1]));
  });
  const best = selectablePairs[0];
  if (!best) return [];
  return best.players.map((player, index) => ({ player, score: best.score, explanation: best.explanations.find((item) => item.playerId === player.id) || calculatePlayerStrength(player, 'doubles', input.players, input.strategy, input.playerRatingBasis || 'edr'), policyEvaluation: best.policyEvaluations[index] }));
}

function shouldUseFemaleFirstSlotOrder(position: RecommendationPosition): boolean {
  return position.lineType !== 'singles'
    && position.requiredPlayerCount === 2
    && position.requiredFemaleCount === 1
    && position.requiredMaleCount === 1;
}

function orderProposedPlayerIdsForPosition(position: RecommendationPosition, playerIds: Array<string | null>, players: RecommendationPlayer[]): Array<string | null> {
  if (!shouldUseFemaleFirstSlotOrder(position)) return playerIds;
  const playerById = new Map(players.map((player) => [player.id, player]));
  const femalePlayerId = playerIds.find((playerId) => playerId && playerById.get(playerId)?.sex === 'female') || null;
  const malePlayerId = playerIds.find((playerId) => playerId && playerById.get(playerId)?.sex === 'male') || null;
  if (!femalePlayerId || !malePlayerId) return playerIds;
  return [femalePlayerId, malePlayerId];
}

function disruptionPenaltyFor(planType: RecommendationPlanType, changedAssignments: number, lineupStatus: LineupStatus, sensitivity: ChangeSensitivity): number {
  const base = planType === 'minimal_disruption' ? 6 : planType === 'balanced' ? 3 : 1.5;
  const statusMultiplier = lineupStatus === 'Published' ? 4 : 1;
  const sensitivityMultiplier = sensitivity === 'conservative' ? 2 : sensitivity === 'flexible' ? 0.7 : 1;
  return changedAssignments * base * statusMultiplier * sensitivityMultiplier;
}

function lineCompatibilityKey(position: RecommendationPosition): string {
  return [
    position.lineType,
    position.requiredPlayerCount,
    position.requiredMaleCount,
    position.requiredFemaleCount,
  ].join(':');
}

function selectedUnitStrength(input: RecommendationInput, position: RecommendationPosition, playerIds: string[]): number {
  const players = playerIds
    .map((playerId) => input.players.find((player) => player.id === playerId))
    .filter((player): player is RecommendationPlayer => Boolean(player));
  if (players.length !== position.requiredPlayerCount) return -1;
  if (position.lineType === 'singles' || players.length === 1) {
    return calculatePlayerStrength(players[0], 'singles', input.players, input.strategy, input.playerRatingBasis || 'edr').score;
  }
  const strengths = players.map((player) => calculatePlayerStrength(player, 'doubles', input.players, input.strategy, input.playerRatingBasis || 'edr').score);
  return strengths.reduce((sum, score) => sum + score, 0) / Math.max(1, strengths.length);
}

function selectedDoublesPairingScore(input: RecommendationInput, playerAId: string, playerBId: string): number {
  const playerA = input.players.find((player) => player.id === playerAId);
  const playerB = input.players.find((player) => player.id === playerBId);
  if (!playerA || !playerB) return Number.NEGATIVE_INFINITY;
  const preference = input.workspaceId ? partnerPreferenceForPair(input.partnerPreferences, input.workspaceId, playerAId, playerBId) : null;
  const compatibility = normalizeCompatibility(preference?.compatibility);
  const pairingRule = normalizePairingRule(preference?.pairing_rule);
  const pairStrength = calculateDoublesPairStrength(playerA, playerB, input.players, input.strategy, compatibility, input.playerRatingBasis || 'edr');
  return pairStrength.score + pairingRulePairedBonus(pairingRule, input.strategy);
}

function validPairForPosition(input: RecommendationInput, position: RecommendationPosition, playerAId: string, playerBId: string): boolean {
  const playerA = input.players.find((player) => player.id === playerAId);
  const playerB = input.players.find((player) => player.id === playerBId);
  if (!playerA || !playerB) return false;
  if (position.requiredMaleCount > 0 && [playerA, playerB].filter((player) => player.sex === 'male').length !== position.requiredMaleCount) return false;
  if (position.requiredFemaleCount > 0 && [playerA, playerB].filter((player) => player.sex === 'female').length !== position.requiredFemaleCount) return false;
  if (hasExcludedPair(input, playerAId, playerBId)) return false;
  const strictPartnerA = hardPartnerRuleForPlayer(input, playerAId, 'schedule_only_as_pair');
  const strictPartnerB = hardPartnerRuleForPlayer(input, playerBId, 'schedule_only_as_pair');
  if (strictPartnerA && strictPartnerA !== playerBId) return false;
  if (strictPartnerB && strictPartnerB !== playerAId) return false;
  return true;
}

function buildBestStrengthOrderedDoublesUnits(
  input: RecommendationInput,
  position: RecommendationPosition,
  playerIds: string[],
): Array<{ playerIds: string[]; strength: number; pairingScore: number }> | null {
  if (playerIds.length === 0 || playerIds.length % 2 !== 0) return null;
  const uniquePlayerIds = [...new Set(playerIds)];
  if (uniquePlayerIds.length !== playerIds.length) return null;
  let bestUnits: Array<{ playerIds: string[]; strength: number; pairingScore: number }> | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;

  const search = (remaining: string[], units: Array<{ playerIds: string[]; strength: number; pairingScore: number }>) => {
    if (remaining.length === 0) {
      const sortedUnits = [...units].sort((a, b) => b.strength - a.strength || a.playerIds.join('|').localeCompare(b.playerIds.join('|')));
      const score = sortedUnits.reduce((sum, unit, index) => sum + unit.strength * (sortedUnits.length - index) + unit.pairingScore * 0.35, 0);
      if (score > bestScore) {
        bestScore = score;
        bestUnits = sortedUnits;
      }
      return;
    }
    const first = remaining[0];
    for (let index = 1; index < remaining.length; index += 1) {
      const second = remaining[index];
      if (!validPairForPosition(input, position, first, second)) continue;
      const pairIds = orderProposedPlayerIdsForPosition(position, [first, second], input.players).filter((playerId): playerId is string => Boolean(playerId));
      const strength = selectedUnitStrength(input, position, pairIds);
      const pairingScore = selectedDoublesPairingScore(input, pairIds[0], pairIds[1]);
      search(
        remaining.filter((playerId) => playerId !== first && playerId !== second),
        [...units, { playerIds: pairIds, strength, pairingScore }],
      );
    }
  };

  search(uniquePlayerIds, []);
  return bestUnits;
}

function reorderSelectedRosterByLineStrength(input: RecommendationInput, orderedPositions: RecommendationPosition[], changes: RecommendationChange[]): void {
  const pinnedPositionIds = new Set(
    input.entries
      .filter((entry) => entry.id && input.pinnedAssignmentIds?.includes(entry.id))
      .map((entry) => entry.leagueConfigurationPositionId),
  );
  const positionsByGroup = new Map<string, RecommendationPosition[]>();
  orderedPositions.forEach((position) => {
    if (pinnedPositionIds.has(position.id)) return;
    const key = lineCompatibilityKey(position);
    positionsByGroup.set(key, [...(positionsByGroup.get(key) || []), position]);
  });

  positionsByGroup.forEach((positions) => {
    if (positions.length < 2) return;
    const selectedUnits = positions
      .map((position) => {
        const positionChanges = changes
          .filter((change) => change.leagueConfigurationPositionId === position.id)
          .sort((a, b) => a.slotNumber - b.slotNumber);
        const playerIds = positionChanges.map((change) => change.newPlayerId).filter((playerId): playerId is string => Boolean(playerId));
        return {
          position,
          playerIds,
          strength: selectedUnitStrength(input, position, playerIds),
        };
      })
      .filter((unit) => unit.playerIds.length === unit.position.requiredPlayerCount);
    if (selectedUnits.length < 2) return;

    const sortedPositions = [...positions].sort((a, b) => a.displayOrder - b.displayOrder || a.positionCode.localeCompare(b.positionCode));
    const canRebuildDoublesPairs = sortedPositions.every((position) => position.lineType !== 'singles' && position.requiredPlayerCount === 2);
    const rebuiltUnits = canRebuildDoublesPairs
      ? buildBestStrengthOrderedDoublesUnits(input, sortedPositions[0], selectedUnits.flatMap((unit) => unit.playerIds))
      : null;
    const sortedUnits = rebuiltUnits && rebuiltUnits.length === sortedPositions.length
      ? rebuiltUnits
      : [...selectedUnits].sort((a, b) => b.strength - a.strength || a.position.positionCode.localeCompare(b.position.positionCode));
    sortedPositions.forEach((position, index) => {
      const unit = sortedUnits[index];
      if (!unit) return;
      const orderedPlayerIds = orderProposedPlayerIdsForPosition(position, unit.playerIds, input.players);
      const positionChanges = changes
        .filter((change) => change.leagueConfigurationPositionId === position.id)
        .sort((a, b) => a.slotNumber - b.slotNumber);
      positionChanges.forEach((change, slotIndex) => {
        const nextPlayerId = orderedPlayerIds[slotIndex] || null;
        change.newPlayerId = nextPlayerId;
        change.changeType = change.oldPlayerId === nextPlayerId ? 'unchanged' : change.oldPlayerId && nextPlayerId ? 'replaced' : nextPlayerId ? 'added' : 'removed';
        change.reason = nextPlayerId
          ? `Selected for this match, then ordered onto ${position.positionCode} by player strength.`
          : `No valid player found for ${position.positionCode} slot ${slotIndex + 1}.`;
        change.scoreImpact = unit.strength;
      });
    });
  });
}

function buildPlan(input: RecommendationInput, planType: RecommendationPlanType): RecommendationPlan {
  const existingBySlot = new Map(input.entries.map((entry) => [`${entry.leagueConfigurationPositionId}:${entry.slotNumber}`, entry]));
  const usedPlayerIds = new Set<string>();
  const changes: RecommendationChange[] = [];
  const ratingExplanations: RatingExplanation[] = [];
  const warnings: string[] = [];
  const objectiveTotals = {
    competitiveness: 0,
    participationFairness: 0,
    singlesOpportunity: 0,
    scheduleSpacing: 0,
    partnerCompatibility: 0,
    homeAwayBalance: 0,
  };
  const orderedPositions = [...input.positions].sort((a, b) => a.displayOrder - b.displayOrder || a.positionCode.localeCompare(b.positionCode));
  if (input.match.opponentRatingBasis === 'avg_edr' && input.match.opponentAverageEdr === null) {
    warnings.push(`${input.match.opponentName || 'Opponent'} is missing opponent average EDR; difficulty data is incomplete.`);
  }

  orderedPositions.forEach((position) => {
    const existingEntries = Array.from({ length: position.requiredPlayerCount }, (_, index) => existingBySlot.get(`${position.id}:${index + 1}`) || null);
    const pinnedBySlot = new Map<number, RecommendationEntry>();
    existingEntries.forEach((entry) => {
      if (!entry?.id || !input.pinnedAssignmentIds?.includes(entry.id)) return;
      const pinnedPlayer = input.players.find((player) => player.id === entry.playerId);
      if (pinnedPlayer?.availabilityStatus === 'not_available') return;
      pinnedBySlot.set(entry.slotNumber, entry);
      usedPlayerIds.add(entry.playerId);
    });

    const selected = choosePlayersForPosition(input, position, usedPlayerIds, planType);
    let selectedIndex = 0;
    const unorderedProposedPlayerIds = Array.from({ length: position.requiredPlayerCount }, (_, index) => {
      const pinnedEntry = pinnedBySlot.get(index + 1);
      if (pinnedEntry) return pinnedEntry.playerId;
      const nextSelected = selected[selectedIndex];
      selectedIndex += 1;
      return nextSelected?.player.id || null;
    });
    const proposedPlayerIds = orderProposedPlayerIdsForPosition(position, unorderedProposedPlayerIds, input.players);
    proposedPlayerIds.forEach((playerId) => {
      if (playerId) usedPlayerIds.add(playerId);
    });

    proposedPlayerIds.forEach((playerId, index) => {
      const oldEntry = existingEntries[index];
      const oldPlayerId = oldEntry?.playerId || null;
      const selectedPlayer = playerId ? input.players.find((player) => player.id === playerId) : undefined;
      const selectedScore = selected.find((item) => item.player.id === playerId);
      if (selectedScore) ratingExplanations.push(selectedScore.explanation);
      if (selectedPlayer?.availabilityStatus === 'maybe') warnings.push(`${selectedPlayer.firstName} ${selectedPlayer.lastName} is tentative.`);
      selectedScore?.policyEvaluation.warnings.forEach((warning) => {
        if (!warnings.includes(warning)) warnings.push(warning);
      });
      changes.push({
        matchEventId: input.match.id,
        leagueConfigurationPositionId: position.id,
        positionCode: position.positionCode,
        slotNumber: index + 1,
        oldPlayerId,
        newPlayerId: playerId,
        changeType: oldPlayerId === playerId ? 'unchanged' : oldPlayerId && playerId ? 'replaced' : playerId ? 'added' : 'removed',
        reason: playerId
          ? `${planType === 'strongest_competitive' ? 'Highest strength fit' : planType === 'minimal_disruption' ? 'Lowest disruption valid fit' : 'Best balanced strategy fit'} for ${position.positionCode}.`
          : `No valid player found for ${position.positionCode} slot ${index + 1}.`,
        scoreImpact: selectedScore?.score || 0,
      });
      if (selectedPlayer) {
        const strength = calculatePlayerStrength(selectedPlayer, position.lineType === 'singles' ? 'singles' : 'doubles', input.players, input.strategy, input.playerRatingBasis || 'edr');
        objectiveTotals.competitiveness += strengthFitScore(input, strength.score);
        objectiveTotals.participationFairness += participationScore(input, selectedPlayer);
        objectiveTotals.singlesOpportunity += position.lineType === 'singles' ? singlesOpportunityScore(selectedPlayer, input.singlesPreferenceByPlayerId?.[selectedPlayer.id] || 'if_possible', true) : 0;
        objectiveTotals.scheduleSpacing += Math.min(selectedPlayer.availabilityStatus === 'maybe' ? 35 : 60, selectedScore?.policyEvaluation.score ?? 60);
        objectiveTotals.partnerCompatibility += 50;
        objectiveTotals.homeAwayBalance += homeAwayScore(selectedPlayer, input.match.matchSiteType);
      }
    });
  });

  reorderSelectedRosterByLineStrength(input, orderedPositions, changes);

  const requiredPlayers = input.positions.reduce((sum, position) => sum + position.requiredPlayerCount, 0);
  const divisor = Math.max(1, requiredPlayers);
  const changedAssignments = changes.filter((change) => change.changeType !== 'unchanged').length;
  const disruptionPenalty = disruptionPenaltyFor(planType, changedAssignments, input.match.lineupStatus, input.strategy.changeSensitivity);
  const breakdown = {
    competitiveness: Math.round(objectiveTotals.competitiveness / divisor),
    participationFairness: Math.round(objectiveTotals.participationFairness / divisor),
    singlesOpportunity: Math.round(objectiveTotals.singlesOpportunity / divisor),
    scheduleSpacing: Math.round(objectiveTotals.scheduleSpacing / divisor),
    partnerCompatibility: Math.round(objectiveTotals.partnerCompatibility / divisor),
    homeAwayBalance: Math.round(objectiveTotals.homeAwayBalance / divisor),
  };
  const weightedScore =
    breakdown.competitiveness * 0.48 +
    breakdown.participationFairness * 0.28 +
    breakdown.singlesOpportunity * 0.08 +
    breakdown.scheduleSpacing * 0.08 +
    breakdown.partnerCompatibility * 0.05 +
    breakdown.homeAwayBalance * 0.03;

  const names: Record<RecommendationPlanType, string> = {
    minimal_disruption: 'Minimal Disruption',
    balanced: 'Balanced',
    strongest_competitive: 'Strongest Competitive',
  };
  return {
    id: `${planType}:${input.match.id}`,
    planType,
    name: names[planType],
    recommended: planType === 'balanced',
    overallScore: Math.max(0, Math.round(weightedScore - disruptionPenalty)),
    objectiveScoreBreakdown: breakdown,
    disruptionPenalty: Math.round(disruptionPenalty),
    changes,
    benefits: [
      changedAssignments === 0 ? 'Current lineup already satisfies the selected constraints.' : `Proposes ${changedAssignments} lineup change${changedAssignments === 1 ? '' : 's'}.`,
      `Uses ${input.strategy.name} team recommendation rules.`,
    ],
    tradeoffs: changedAssignments > 0 ? [`Disruption penalty: ${Math.round(disruptionPenalty)}.`] : [],
    warnings,
    explanations: [
      `Strategy profile: ${input.strategy.name} v${input.strategy.version}.`,
      `CourtCaptain first determines who should play from availability and participation, then fits strength to match difficulty and line order.`,
      input.strategy.enforceTargetMatches ? `Enforce Target Matches is on with ${targetVariance(input) === 0 ? 'exact' : `±${targetVariance(input)}`} variance.` : 'Baseline participation fairness is on; target matches are not enforced as hard goals.',
      input.playerRatingBasis === 'tier' ? 'Player strength uses Tier 1, Tier 2, and Tier 3.' : 'Player strength uses EDR where available.',
      input.crossTeamPolicyTeamIds?.includes(input.match.teamId) ? 'Cross-team scheduling policy was evaluated after the core team recommendation.' : 'No cross-team scheduling policy applies to this team recommendation.',
    ],
    ratingExplanations,
  };
}

export function generateLineupRecommendationAnalysis(input: RecommendationInput): RecommendationAnalysis {
  const validation = validateStrategyProfile(input.strategy);
  const requiredPlayers = input.positions.reduce((sum, position) => sum + position.requiredPlayerCount, 0);
  const unresolvedConstraints = [...validation.errors];
  if (input.match.matchStatus === 'Completed') unresolvedConstraints.push('Completed match lineups are read-only.');
  const excludedBecauseUnavailable = input.players.filter((player) => player.availabilityStatus === 'not_available').length;
  if (unresolvedConstraints.length > 0) {
    return {
      strategy: input.strategy,
      generatedAt: new Date().toISOString(),
      currentStateSummary: {
        assignedPlayers: input.entries.length,
        requiredPlayers,
        openSlots: Math.max(0, requiredPlayers - input.entries.length),
        readOnly: input.match.matchStatus === 'Completed',
        excludedBecauseUnavailable,
      },
      plans: [],
      unresolvedConstraints,
    };
  }
  const plans = (['minimal_disruption', 'balanced', 'strongest_competitive'] as RecommendationPlanType[])
    .map((planType) => buildPlan(input, planType))
    .sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0) || b.overallScore - a.overallScore || a.name.localeCompare(b.name));
  return {
    strategy: input.strategy,
    generatedAt: new Date().toISOString(),
    currentStateSummary: {
      assignedPlayers: input.entries.length,
      requiredPlayers,
      openSlots: Math.max(0, requiredPlayers - input.entries.length),
      readOnly: false,
      excludedBecauseUnavailable,
    },
    plans,
    unresolvedConstraints,
  };
}

function dateOnly(value: string | null | undefined): string {
  return (value || '').slice(0, 10);
}

function playerName(player: RecommendationPlayer): string {
  return `${player.firstName} ${player.lastName}`.trim();
}

function playableAvailability(
  status: AvailabilityStatus | undefined,
  unknownPolicy: UnknownAvailabilityPolicy,
  includeTentativePlayers: boolean,
): { status: AvailabilityStatus; warning: string | null } {
  const value = status || 'unknown';
  if (value === 'unknown') {
    if (unknownPolicy === 'exclude') return { status: 'not_available', warning: 'Unknown availability excluded from planning.' };
    if (unknownPolicy === 'include_with_warning') return { status: 'unknown', warning: 'Unknown availability included with warning.' };
    return { status: 'available', warning: 'Unknown availability assumed available for planning only.' };
  }
  if (value === 'maybe' && !includeTentativePlayers) return { status: 'not_available', warning: 'Tentative availability excluded from this season plan.' };
  return { status: value, warning: null };
}

function seasonPlanStrategy(base: RecommendationStrategyProfile, planType: SeasonRecommendationPlanType): RecommendationStrategyProfile {
  if (planType === 'participation_focused_season') {
    return {
      ...base,
      competitivenessWeight: 15,
      participationFairnessWeight: 35,
      singlesOpportunityWeight: 25,
      scheduleSpacingWeight: 10,
      partnerCompatibilityWeight: 10,
      homeAwayBalanceWeight: 5,
    };
  }
  if (planType === 'strongest_competitive_season') {
    return {
      ...base,
      competitivenessWeight: 60,
      participationFairnessWeight: 15,
      singlesOpportunityWeight: 5,
      scheduleSpacingWeight: 5,
      partnerCompatibilityWeight: 10,
      homeAwayBalanceWeight: 5,
    };
  }
  if (planType === 'minimal_change_season') {
    return { ...base, changeSensitivity: 'conservative' };
  }
  return base;
}

function singleMatchPlanType(planType: SeasonRecommendationPlanType): RecommendationPlanType {
  if (planType === 'strongest_competitive_season') return 'strongest_competitive';
  if (planType === 'minimal_change_season') return 'minimal_disruption';
  return 'balanced';
}

function priorityScore(priority: RecommendationMatch['planningPriority']): number {
  if (priority === 'must_win') return 3;
  if (priority === 'important') return 2;
  if (priority === 'development') return -1;
  return 0;
}

function seasonPlanningOrder(a: SeasonPlanningMatch, b: SeasonPlanningMatch): number {
  const difficultyDifference = opponentDifficultyScore(b) - opponentDifficultyScore(a);
  if (difficultyDifference !== 0) return difficultyDifference;
  const priorityDifference = priorityScore(b.planningPriority) - priorityScore(a.planningPriority);
  if (priorityDifference !== 0) return priorityDifference;
  return dateOnly(a.matchDate).localeCompare(dateOnly(b.matchDate)) || a.id.localeCompare(b.id);
}

function filterSeasonMatches(request: SeasonPlanningRequest): { included: SeasonPlanningMatch[]; excluded: Array<{ matchId: string; reason: string }> } {
  const selected = request.selectedMatchIds?.length ? new Set(request.selectedMatchIds) : null;
  const excluded: Array<{ matchId: string; reason: string }> = [];
  const included = request.matches.filter((match) => {
    if (selected && !selected.has(match.id)) return false;
    if (match.matchStatus === 'Completed') {
      excluded.push({ matchId: match.id, reason: 'Completed matches are historical inputs and are not modified.' });
      return false;
    }
    if (match.matchStatus === 'Cancelled') {
      excluded.push({ matchId: match.id, reason: 'Cancelled matches are excluded.' });
      return false;
    }
    if (match.matchPhase === 'postseason') {
      excluded.push({ matchId: match.id, reason: 'Postseason matches are excluded from regular-season baseline planning.' });
      return false;
    }
    if (match.matchStatus === 'Completed') {
      excluded.push({ matchId: match.id, reason: 'Completed match lineups are protected.' });
      return false;
    }
    if (match.lineupStatus === 'Published' && !request.includePublished) {
      excluded.push({ matchId: match.id, reason: 'Published lineups are protected by default.' });
      return false;
    }
    return true;
  });
  included.sort((a, b) => dateOnly(a.matchDate).localeCompare(dateOnly(b.matchDate)) || priorityScore(b.planningPriority) - priorityScore(a.planningPriority) || a.id.localeCompare(b.id));
  return { included, excluded };
}

function calculateAvailabilityScarcity(request: SeasonPlanningRequest, matches: SeasonPlanningMatch[]): AvailabilityScarcity[] {
  const hasSinglesLines = request.positions.some((position) => position.lineType === 'singles');
  return request.players
    .filter((player) => !isDesignatedSubstitute({ roster_role: player.rosterRole }))
    .map((player) => {
      let availableMatches = 0;
      let tentativeMatches = 0;
      let unknownMatches = 0;
      matches.forEach((match) => {
        const status = match.availabilityByPlayerId[player.id] || 'unknown';
        if (status === 'available') availableMatches += 1;
        else if (status === 'maybe') tentativeMatches += 1;
        else if (status === 'unknown') unknownMatches += 1;
      });
      const matchesNeededToTarget = Math.max(0, (player.targetMatches || 0) - player.completedPlayed);
      const possible = availableMatches + tentativeMatches + (request.unknownAvailabilityPolicy === 'exclude' ? 0 : unknownMatches);
      const targetAchievable = possible >= matchesNeededToTarget;
      const urgencyScore = Math.max(0, Math.min(100, matchesNeededToTarget * 18 + (matchesNeededToTarget - possible) * 20 + (matches.length - availableMatches) * 3));
      const explanation = `${playerName(player)} needs ${matchesNeededToTarget} match${matchesNeededToTarget === 1 ? '' : 'es'} to reach target and is available for ${availableMatches} of ${matches.length} selected match${matches.length === 1 ? '' : 'es'}.`;
      return {
        playerId: player.id,
        remainingSelectedMatches: matches.length,
        availableMatches,
        tentativeMatches,
        unknownMatches,
        eligibleSinglesOpportunities: hasSinglesLines && (request.singlesPreferenceByPlayerId?.[player.id] || 'if_possible') !== 'no' ? availableMatches + tentativeMatches : 0,
        matchesNeededToTarget,
        targetAchievable,
        urgencyScore,
        explanation,
      };
    })
    .sort((a, b) => b.urgencyScore - a.urgencyScore || a.playerId.localeCompare(b.playerId));
}

function matchConfidence(match: SeasonPlanningMatch, changes: RecommendationChange[], playersById: Map<string, RecommendationPlayer>, availabilityByPlayerId: Record<string, AvailabilityStatus | undefined>): { confidence: PlanConfidence; drivers: string[]; uncertainAssignments: number } {
  const drivers: string[] = [];
  let uncertainAssignments = 0;
  let missingRatings = 0;
  changes.filter((change) => change.matchEventId === match.id && change.newPlayerId).forEach((change) => {
    const player = playersById.get(change.newPlayerId || '');
    const availability = availabilityByPlayerId[change.newPlayerId || ''] || 'unknown';
    if (availability === 'maybe' || availability === 'unknown') uncertainAssignments += 1;
    if (player && player.estimatedDynamicRating === null && player.utrSinglesRating === null && player.utrDoublesRating === null) missingRatings += 1;
  });
  if (uncertainAssignments > 0) drivers.push(`${uncertainAssignments} tentative or unknown assignment${uncertainAssignments === 1 ? '' : 's'}.`);
  if (missingRatings > 0) drivers.push(`${missingRatings} assignment${missingRatings === 1 ? '' : 's'} use rating fallback data.`);
  const daysOut = Math.max(0, Math.round((Date.parse(match.matchDate) - Date.now()) / (24 * 60 * 60 * 1000)));
  if (daysOut > 45) drivers.push('Match is far enough away that availability may change.');
  const confidence: PlanConfidence = uncertainAssignments > 1 || missingRatings > 2 ? 'Low' : drivers.length > 0 ? 'Moderate' : 'High';
  return { confidence, drivers, uncertainAssignments };
}

function combineConfidence(items: MatchSeasonProjection[]): PlanConfidence {
  if (items.some((item) => item.confidence === 'Low')) return 'Low';
  if (items.some((item) => item.confidence === 'Moderate')) return 'Moderate';
  return 'High';
}

function buildSeasonPlan(request: SeasonPlanningRequest, matches: SeasonPlanningMatch[], scarcity: AvailabilityScarcity[], planType: SeasonRecommendationPlanType): SeasonRecommendationPlan {
  const strategy = seasonPlanStrategy(request.strategy, planType);
  const playersById = new Map(request.players.map((player) => [player.id, player]));
  const futureByPlayer = new Map(request.players.map((player) => [player.id, { singles: 0, doubles: 0, home: 0, away: 0, neutral: 0 }]));
  const allChanges: RecommendationChange[] = [];
  const warnings: string[] = [];
  const explanations: string[] = [
    'Extends the match-level recommendation engine across the selected season window.',
    'Published lineups remain protected unless explicitly included. Completed match lineups are always read-only.',
  ];
  const matchProjections: MatchSeasonProjection[] = [];
  const sameDayAssignments = new Map<string, Set<string>>();
  const scarcityByPlayer = new Map(scarcity.map((item) => [item.playerId, item]));
  const planningMatches = [...matches].sort(seasonPlanningOrder);

  planningMatches.forEach((match, matchIndex) => {
    const plannedPlayers = request.players.filter((player) => !isDesignatedSubstitute({ roster_role: player.rosterRole })).map((player) => {
      const future = futureByPlayer.get(player.id) || { singles: 0, doubles: 0, home: 0, away: 0, neutral: 0 };
      const availability = playableAvailability(match.availabilityByPlayerId[player.id], request.unknownAvailabilityPolicy || 'exclude', request.includeTentativePlayers ?? true);
      const seasonRemainingOpportunities = planningMatches.slice(matchIndex).reduce((count, remainingMatch) => {
        const remainingAvailability = playableAvailability(remainingMatch.availabilityByPlayerId[player.id], request.unknownAvailabilityPolicy || 'exclude', request.includeTentativePlayers ?? true);
        return count + (remainingAvailability.status === 'not_available' ? 0 : 1);
      }, 0);
      if (availability.warning && availability.status !== 'not_available') warnings.push(`${playerName(player)} vs ${match.opponentName}: ${availability.warning}`);
      return {
        ...player,
        availabilityStatus: availability.status,
        futureSingles: future.singles,
        futureDoubles: future.doubles,
        homeMatches: (player.homeMatches || 0) + future.home,
        awayMatches: (player.awayMatches || 0) + future.away,
        neutralMatches: (player.neutralMatches || 0) + future.neutral,
        totalAssignmentsForBalance: (player.totalAssignmentsForBalance || 0) + future.home + future.away + future.neutral,
        targetMatches: player.targetMatches || 0,
        seasonRemainingOpportunities,
      };
    });
    const preserveExistingMatchAssignments =
      (match.lineupStatus === 'Draft' && request.preserveExistingDraftAssignments)
      || (match.lineupStatus === 'Published' && request.preservePublishedAssignments);
    const explicitPinnedAssignmentIds = request.preservePinnedAssignments === false ? [] : request.pinnedAssignmentIds || [];
    const preservedExistingAssignmentIds = preserveExistingMatchAssignments
      ? match.entries
        .filter((entry) => {
          const availability = playableAvailability(match.availabilityByPlayerId[entry.playerId], request.unknownAvailabilityPolicy || 'exclude', request.includeTentativePlayers ?? true);
          return availability.status !== 'not_available';
        })
        .map((entry) => entry.id)
        .filter((id): id is string => Boolean(id))
      : [];
    const pinnedAssignmentIds = [...explicitPinnedAssignmentIds, ...preservedExistingAssignmentIds];
    const plan = buildPlan({
      match,
      positions: request.positions,
      players: plannedPlayers,
      entries: match.entries,
      partnerPreferences: request.partnerPreferences,
      workspaceId: request.workspaceId,
      strategy,
      playerRatingBasis: request.playerRatingBasis,
      singlesPreferenceByPlayerId: request.singlesPreferenceByPlayerId,
      pinnedAssignmentIds,
      excludedPlayerMatchPairs: request.excludedPlayerMatchPairs,
      respectTargetMatchCounts: strategy.enforceTargetMatches === true,
    }, singleMatchPlanType(planType));
    allChanges.push(...plan.changes);
    warnings.push(...plan.warnings);

    plan.changes.filter((change) => change.newPlayerId).forEach((change) => {
      const position = request.positions.find((item) => item.id === change.leagueConfigurationPositionId);
      const future = futureByPlayer.get(change.newPlayerId || '');
      if (!future || !position) return;
      if (position.lineType === 'singles') future.singles += 1;
      else future.doubles += 1;
      if (match.matchSiteType === 'Home') future.home += 1;
      else if (match.matchSiteType === 'Away') future.away += 1;
      else future.neutral += 1;
      const dayKey = `${change.newPlayerId}:${dateOnly(match.matchDate)}`;
      const assignedMatches = sameDayAssignments.get(dayKey) || new Set<string>();
      assignedMatches.add(match.id);
      sameDayAssignments.set(dayKey, assignedMatches);
    });

    const confidence = matchConfidence(match, plan.changes, playersById, match.availabilityByPlayerId);
    matchProjections.push({
      matchId: match.id,
      matchDate: match.matchDate,
      opponentName: match.opponentName,
      lineupStatus: match.lineupStatus,
      editable: match.matchStatus !== 'Completed' && (match.lineupStatus !== 'Published' || Boolean(request.includePublished)),
      confidence: confidence.confidence,
      confidenceDrivers: confidence.drivers,
      changedAssignments: plan.changes.filter((change) => change.changeType !== 'unchanged').length,
      uncertainAssignments: confidence.uncertainAssignments,
    });
  });

  matchProjections.sort((a, b) => dateOnly(a.matchDate).localeCompare(dateOnly(b.matchDate)) || a.matchId.localeCompare(b.matchId));

  const playerProjections = request.players.map((player) => {
    const future = futureByPlayer.get(player.id) || { singles: 0, doubles: 0, home: 0, away: 0, neutral: 0 };
    const proposedFuture = future.singles + future.doubles;
    const projectedTotalValue = player.completedPlayed + proposedFuture;
    return {
      playerId: player.id,
      playerName: playerName(player),
      targetMatches: player.targetMatches,
      completedPlayed: player.completedPlayed,
      proposedFuture,
      proposedSingles: future.singles,
      proposedDoubles: future.doubles,
      projectedTotal: projectedTotalValue,
      projectedDelta: projectedTotalValue - player.targetMatches,
      scarcity: scarcityByPlayer.get(player.id) || {
        playerId: player.id,
        remainingSelectedMatches: matches.length,
        availableMatches: 0,
        tentativeMatches: 0,
        unknownMatches: 0,
        eligibleSinglesOpportunities: 0,
        matchesNeededToTarget: Math.max(0, player.targetMatches - player.completedPlayed),
        targetAchievable: false,
        urgencyScore: 0,
        explanation: `${playerName(player)} has no selected future opportunities.`,
      },
    };
  }).sort((a, b) => a.projectedDelta - b.projectedDelta || a.playerName.localeCompare(b.playerName));

  sameDayAssignments.forEach((assignedMatches, key) => {
    if (assignedMatches.size <= 1) return;
    const playerId = key.split(':')[0];
    const player = playersById.get(playerId);
    warnings.push(`${player ? playerName(player) : 'A player'} is assigned to ${assignedMatches.size} matches on the same calendar day.`);
  });

  const materialIssues = playerProjections
    .filter((projection) => projection.projectedDelta <= -2 || (!projection.scarcity.targetAchievable && projection.projectedDelta < 0))
    .map((projection) => `${projection.playerName} is projected ${Math.abs(projection.projectedDelta)} match${Math.abs(projection.projectedDelta) === 1 ? '' : 'es'} below target.`);
  const changedAssignments = allChanges.filter((change) => change.changeType !== 'unchanged').length;
  const confidence = combineConfidence(matchProjections);
  const score = Math.max(0, Math.round(
    100
    - materialIssues.length * 8
    - matchProjections.reduce((sum, item) => sum + (item.confidence === 'Low' ? 6 : item.confidence === 'Moderate' ? 3 : 0), 0)
    - (planType === 'minimal_change_season' ? changedAssignments * 0.5 : changedAssignments * 0.25),
  ));
  const names: Record<SeasonRecommendationPlanType, string> = {
    balanced_season: 'Balanced Season Plan',
    strongest_competitive_season: 'Strongest Competitive Season',
    participation_focused_season: 'Participation-Focused Season',
    minimal_change_season: 'Minimal Change Season Plan',
  };
  return {
    id: `${planType}:${request.teamId}:${request.seasonId}:${matches.map((match) => match.id).join('|')}`,
    planType,
    name: names[planType],
    recommended: planType === 'balanced_season',
    overallScore: score,
    confidence,
    confidenceDrivers: [...new Set(matchProjections.flatMap((item) => item.confidenceDrivers))],
    changes: allChanges,
    matchProjections,
    playerProjections,
    materialIssues,
    benefits: [
      `Covers ${matches.length} selected match${matches.length === 1 ? '' : 'es'} as one season plan.`,
      changedAssignments === 0 ? 'No lineup changes are required for this plan.' : `Coordinates ${changedAssignments} captain-reviewed assignment change${changedAssignments === 1 ? '' : 's'}.`,
    ],
    tradeoffs: planType === 'strongest_competitive_season'
      ? ['Competitive strength is prioritized over equal distribution when constraints compete.']
      : planType === 'participation_focused_season'
        ? ['Participation targets may take priority over the strongest available line on some matches.']
        : planType === 'minimal_change_season'
          ? ['Preserves more current work, so fairness improvements may be smaller.']
          : [],
    warnings: [...new Set(warnings)],
    explanations,
  };
}

export function generateSeasonPlanningAnalysis(request: SeasonPlanningRequest): SeasonPlanningAnalysis {
  const validation = validateStrategyProfile(request.strategy);
  const { included, excluded } = filterSeasonMatches(request);
  const scarcity = calculateAvailabilityScarcity(request, included);
  const unresolvedConstraints = [...validation.errors];
  if (included.length === 0) unresolvedConstraints.push('No editable regular-season matches are available for season planning.');
  if (unresolvedConstraints.length > 0) {
    return {
      mode: request.mode,
      strategy: request.strategy,
      generatedAt: new Date().toISOString(),
      teamId: request.teamId,
      seasonId: request.seasonId,
      includedMatchIds: included.map((match) => match.id),
      excludedMatches: excluded,
      availabilityScarcity: scarcity,
      plans: [],
      unresolvedConstraints,
    };
  }

  const planTypes: SeasonRecommendationPlanType[] = ['balanced_season', 'strongest_competitive_season', 'participation_focused_season', 'minimal_change_season'];
  const seenSignatures = new Set<string>();
  const plans = planTypes
    .map((planType) => buildSeasonPlan(request, included, scarcity, planType))
    .filter((plan) => {
      const signature = plan.changes.map((change) => `${change.matchEventId}:${change.leagueConfigurationPositionId}:${change.slotNumber}:${change.newPlayerId || ''}`).join('|');
      if (seenSignatures.has(signature)) return false;
      seenSignatures.add(signature);
      return true;
    })
    .sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0) || b.overallScore - a.overallScore || a.name.localeCompare(b.name));

  if (plans.length < planTypes.length && plans[0]) {
    plans[0].warnings.push('Some season alternatives were materially identical for the selected matches and were collapsed.');
  }

  return {
    mode: request.mode,
    strategy: request.strategy,
    generatedAt: new Date().toISOString(),
    teamId: request.teamId,
    seasonId: request.seasonId,
    includedMatchIds: included.map((match) => match.id),
    excludedMatches: excluded,
    availabilityScarcity: scarcity,
    plans,
    unresolvedConstraints: [],
  };
}
